import { usePage } from "@inertiajs/react"
import { useCallback } from "react"

import type { TourPage } from "@/components/demo-tour/sections"
import { TOUR_SECTIONS } from "@/components/demo-tour/sections"
import type { DemoTourState } from "@/components/demo-tour/tour-engine"
import TourEngine from "@/components/demo-tour/tour-engine"
import {
  planMarketingAnalysisImpactReportDemoTourPath,
  planMarketingAnalysisImpactReportPath,
  planMarketingAnalysisPrePlanQuestionsPath,
  planMarketingAnalysisResultPath,
  planMarketingPlanPath,
} from "@/routes"

interface DemoTourPageProps {
  share_token?: string
  current_account?: { uid?: string }
  marketing_analysis?: {
    uid?: string
    demo_tour_state?: DemoTourState | null
    default_marketing_plan_uid?: string | null
  }
  [key: string]: unknown
}

/** The plan-scope guided demo tour (Impact Report through Growth Plan). */
export default function DemoTourController() {
  const page = usePage<DemoTourPageProps>()
  const analysis = page.props.marketing_analysis
  const agencyUid = page.props.current_account?.uid
  const analysisUid = analysis?.uid
  const planUid = analysis?.default_marketing_plan_uid

  // demo_tour_state is only ever emitted for demo-mode analyses, so its
  // presence is the enablement gate. Share-mode viewers never get the tour.
  const enabled = Boolean(
    analysisUid &&
      agencyUid &&
      !page.props.share_token &&
      analysis?.demo_tour_state !== undefined &&
      analysis?.demo_tour_state !== null,
  )

  const pathForPage = useCallback(
    (target: TourPage): string | null => {
      if (!agencyUid || !analysisUid) return null
      switch (target.kind) {
        case "impact_report":
          return planMarketingAnalysisImpactReportPath(agencyUid, analysisUid)
        case "result":
          return planMarketingAnalysisResultPath(
            agencyUid,
            analysisUid,
            target.slug,
          )
        case "pre_plan_questions":
          return planMarketingAnalysisPrePlanQuestionsPath(
            agencyUid,
            analysisUid,
          )
        case "marketing_plan":
          // A demo plan re-gates through the pre-plan questions on every
          // entry unless it carries from_pre_plan — without it, skipping that
          // part bounced the tour back to the questions page.
          return planUid
            ? planMarketingPlanPath(agencyUid, planUid, { from_pre_plan: 1 })
            : null
        default:
          return null
      }
    },
    [agencyUid, analysisUid, planUid],
  )

  // The plan switcher swaps the plan uid in the URL mid-page; any
  // /plan/marketing_plans/* URL still satisfies a marketing_plan step, so the
  // engine doesn't yank the viewer back to the default plan.
  const pageMatches = useCallback(
    (target: TourPage, path: string): boolean => {
      if (target.kind === "marketing_plan") {
        return path.includes("/plan/marketing_plans/")
      }
      const required = pathForPage(target)?.replace(/\/$/, "")
      return required !== null && required !== undefined && path === required
    },
    [pathForPage],
  )

  const isAutoStartPath = useCallback(
    (path: string): boolean => {
      const impactPath = pathForPage({ kind: "impact_report" })
      return impactPath !== null && path === impactPath.replace(/\/$/, "")
    },
    [pathForPage],
  )

  if (!enabled || !agencyUid || !analysisUid) return null

  return (
    <TourEngine
      sections={TOUR_SECTIONS}
      savedState={analysis?.demo_tour_state}
      persistUrl={planMarketingAnalysisImpactReportDemoTourPath(
        agencyUid,
        analysisUid,
      )}
      pathForPage={pathForPage}
      pageMatches={pageMatches}
      isAutoStartPath={isAutoStartPath}
    />
  )
}
